import type { User as FirebaseUser } from 'firebase/auth'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { IncomingMessage } from 'http'
import Cookies, { CookieSetOptions } from 'universal-cookie'

export const AUTH_COOKIE_NAME = 'authIdToken'
export const SESSION_COOKIE_NAME = 'sessionToken'

export interface SessionCookie {
  token: string
  createdAt: number
  expiresAt: number
}

type MaybeSessionCookie = SessionCookie | null
type MaybeTokenCookie = string | null

const AUTH_PERSISTENCE_KEY = 'authPersisence'
export enum AuthPersistence {
  /**
   * Firebase stores session information in session storage, so when the window is closed, the session is gone
   */
  SESSION = 'session',

  /**
   * Firebase stores the session longer-term in localstorage or indexedDB
   */
  LOCAL = 'local'
}

const COOKIE_OPTIONS: CookieSetOptions = {
  httpOnly: false,
  path: '/',
  sameSite: 'strict',
  secure: process.env.NODE_ENV === 'production'
}

const firebaseIsLoaded = async (): Promise<boolean> => {
  const auth = getAuth()
  return new Promise((resolve) => {
    onAuthStateChanged(auth, () => {
      resolve(true)
    })
  })
}

let firebaseCheck: Promise<boolean> | null = null

const shouldPersistSession = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  } else {
    return window.sessionStorage.getItem(AUTH_PERSISTENCE_KEY) !== AuthPersistence.SESSION
  }
}

export const storeAuthPersistence = (persistence: AuthPersistence) => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(AUTH_PERSISTENCE_KEY, persistence)
  }
}

export const clearAuthPersistence = () => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(AUTH_PERSISTENCE_KEY)
  }
}

const cookiesInstance = (req?: IncomingMessage) => {
  if (req) {
    return new Cookies(req.headers.cookie)
  }

  return new Cookies()
}

export const deleteAuthCookies = (req?: IncomingMessage) => {
  const cookies = cookiesInstance(req)
  cookies.remove(SESSION_COOKIE_NAME, COOKIE_OPTIONS)
  cookies.remove(AUTH_COOKIE_NAME, COOKIE_OPTIONS)
  clearAuthPersistence()
}

export const setAuthIdCookie = async (user: FirebaseUser | null, req?: IncomingMessage): Promise<string | undefined> => {
  const cookies = cookiesInstance(req)

  if (user) {
    const idToken = await user.getIdToken()
    if (shouldPersistSession()) {
      cookies.set(AUTH_COOKIE_NAME, idToken, COOKIE_OPTIONS)
    }
    return idToken
  } else {
    deleteAuthCookies(req)
    return undefined
  }
}

export const setSessionCookie = (payload: any, req?: IncomingMessage) => {
  const cookies = cookiesInstance(req)

  cookies.set(SESSION_COOKIE_NAME, JSON.stringify(payload), {
    expires: new Date(payload.expiresAt),
    ...COOKIE_OPTIONS
  })
}

export const getAuthIdTokenFromCookie = (req?: IncomingMessage): string | undefined => {
  const cookies = cookiesInstance(req)
  return cookies.get(AUTH_COOKIE_NAME) as MaybeTokenCookie
}

export const getAuthIdToken = async (req?: IncomingMessage): Promise<string | undefined> => {
  if (req) {
    return getAuthIdTokenFromCookie(req)
  } else {
    /**
     * Wait for firebase to be loaded fully client side before trying to get an id token. If we don't
     * wait here, when handling a request which has been marked as being logged-in from the SSR logic,
     * the client can fail to get a fresh token because firebase hasn't caught up yet. By waiting for
     * the initial auth state change from firebase, we can ensure it's fully aware of whether you're logged
     * in or not.
     *
     * The promise is memoised on the window so that its only run once, since this is really a first-load
     * problem.
     */
    if (!firebaseCheck) {
      firebaseCheck = firebaseIsLoaded()
    }
    await firebaseCheck

    const auth = getAuth()
    const authToken = await setAuthIdCookie(auth.currentUser, req)
    return authToken
  }
}

export const getSessionCookieToken = (req?: IncomingMessage): string | undefined => {
  const cookies = cookiesInstance(req)
  const sessionCookie = cookies.get(SESSION_COOKIE_NAME) as MaybeSessionCookie
  return sessionCookie?.token
}

export const shouldFetchCookieToken = (req?: IncomingMessage): boolean => {
  const cookies = cookiesInstance(req)
  const sessionCookie = cookies.get(SESSION_COOKIE_NAME)

  if (sessionCookie) {
    return sessionCookie.createdAt <= Date.now() - 60 * 60 * 2 * 1000 // 2 hours
  }

  return shouldPersistSession()
}
